import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { DoorConfig, DoorTypeId } from '@/lib/configurator/options'

import Image from 'next/image'
import Link from 'next/link'

import {
    ArrowRight,
    ArrowUpRight,
    BadgeEuro,
    Box,
    Hammer,
    Ruler,
    ShieldCheck,
    Truck
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Reveal from '@/components/generals/reveal'
import HeroDoor from '@/components/landing/hero-door'
import DoorDrawing from '@/components/configurator/door-drawing'
import OrderProgress from '@/components/orders/order-progress'
import { CtaBand, FaqList, GlassTile, ProcessList, SectionHeading } from '@/components/landing/blocks'
import { DIVISIONS, DOOR_TYPES, FINISHES, GLASS_TYPES, RAL_COLORS } from '@/lib/configurator/options'
import { applyChange, applyDivisionDefaults, defaultConfig, toCustomBars } from '@/lib/configurator/validate'
import { DEFAULT_PRICES, getFromPrices } from '@/lib/configurator/pricing'

interface HomeProps {
    fromPrices: Record<DoorTypeId, number>
}

const BASE_DOOR = defaultConfig()

const DOOR_PREVIEWS: Record<DoorTypeId, DoorConfig> = {
    'draaideur': applyChange(BASE_DOOR, { doorType: 'draaideur', division: 'horizontaal', rows: 3 }),
    'dubbele-draaideur': applyChange(BASE_DOOR, { doorType: 'dubbele-draaideur', width: 1600, division: 'raster', rows: 4, cols: 1 }),
    'taatsdeur': applyChange(BASE_DOOR, { doorType: 'taatsdeur', width: 1000, height: 2400, division: 'verticaal', cols: 1, glass: 'mat' }),
    'schuifdeur': applyChange(BASE_DOOR, { doorType: 'schuifdeur', width: 1000, division: 'strak' })
}

const CUSTOM_SHOWCASE = toCustomBars(BASE_DOOR, [
    { orientation: 'h', at: 820, from: null, to: null },
    { orientation: 'h', at: 1640, from: null, to: null },
    { orientation: 'v', at: 445, from: 1640, to: null }
])

const DIVISION_SHOWCASE = DIVISIONS.map(division => ({
    division,
    config: division.id === 'eigen' ? CUSTOM_SHOWCASE : applyDivisionDefaults(BASE_DOOR, division.id)
}))

const RAL_PREVIEW = ['RAL 7016', 'RAL 9001', 'RAL 8019', 'RAL 6009', 'RAL 5011', 'RAL 3009']

const USPS = [
    { icon: Ruler, title: 'Inmeten aan huis', text: 'Altijd inbegrepen, tot op de millimeter.' },
    { icon: BadgeEuro, title: 'Prijs per m²', text: 'Elke keuze meteen doorgerekend.' },
    { icon: Hammer, title: 'Handgemaakt', text: 'Gelast, geschuurd en gepoedercoat.' },
    { icon: Truck, title: 'Eigen montage', text: 'Geplaatst door onze specialisten.' }
]

const Home: FC<HomeProps> = (): ReactNode => {
    return (
        <Layout transparentHeader>
            <Seo description='Maatwerk stalen binnendeuren met glas. Bereken direct de prijs van uw stalen deur in onze 3D-configurator, kies glas, greep en RAL-kleur en laat hem inmeten en plaatsen.' />

            <section id='hero' className='relative overflow-hidden bg-ink text-ink-foreground'>
                <div className='steel-grid pointer-events-none absolute inset-0 opacity-[0.08]' aria-hidden='true' />
                <div className='pointer-events-none absolute -top-40 right-[-10%] size-[46rem] rounded-full bg-[radial-gradient(circle,color-mix(in_oklch,var(--primary)_26%,transparent),transparent_65%)]' aria-hidden='true' />

                <div className='relative mx-auto grid min-h-[100svh] max-w-[1440px] items-center gap-12 px-5 pt-28 pb-16 sm:px-8 lg:grid-cols-[1fr_1.05fr] lg:px-12 lg:pt-32'>
                    <div className='max-w-xl'>
                        <p className='ad-rise eyebrow'>Stalen binnendeuren op maat</p>

                        <h1 className='ad-rise mt-6 font-display text-[3rem] leading-[1] sm:text-[4.2rem] xl:text-[5rem]' style={{ animationDelay: '80ms' }}>
                            Bereken direct de{' '}<em className='text-primary not-italic sm:italic'>prijs</em> van uw stalendeur!
                        </h1>

                        <p className='ad-rise mt-7 max-w-lg text-lg leading-relaxed text-ink-foreground/70' style={{ animationDelay: '160ms' }}>
                            Ontwerp uw stalen deur in 3D en zie meteen wat hij kost, berekend per vierkante meter. De glasverdeling is altijd inbegrepen. Wij meten in, maken en plaatsen.
                        </p>

                        <div className='ad-rise mt-10 flex flex-col gap-3 sm:flex-row' style={{ animationDelay: '240ms' }}>
                            <Link href='/configurator' className='inline-flex h-13 items-center justify-center gap-2 rounded-sm bg-primary px-7 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/85'>
                                Bereken uw prijs in 3D
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </Link>

                            <Link href='/collectie' className='inline-flex h-13 items-center justify-center rounded-sm border border-white/25 px-7 text-sm font-semibold transition-colors hover:bg-white/10'>
                                Bekijk de collectie
                            </Link>
                        </div>

                        <ul className='ad-rise mt-12 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-white/10 pt-8 text-sm' style={{ animationDelay: '320ms' }}>
                            {USPS.map(item => {
                                const Icon = item.icon

                                return (
                                    <li key={item.title} className='flex items-start gap-3'>
                                        <Icon className='mt-0.5 size-5 shrink-0 text-primary' aria-hidden='true' />
                                        <span>
                                            <span className='block font-semibold'>{item.title}</span>
                                            <span className='block text-xs text-ink-foreground/55'>{item.text}</span>
                                        </span>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>

                    <div className='ad-rise relative' style={{ animationDelay: '200ms' }}>
                        <div className='relative aspect-[4/5] overflow-hidden rounded-md shadow-[0_40px_120px_-40px_rgba(242,106,27,0.35)] ring-1 ring-white/10 sm:aspect-[5/5] lg:aspect-[4/5]'>
                            <HeroDoor />

                            <div className='pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/70 to-transparent p-5'>
                                <div>
                                    <p className='text-[11px] font-semibold tracking-[0.2em] text-primary uppercase'>Live in 3D</p>
                                    <p className='mt-1 text-sm text-white'>Zwarte draaideur RAL 9005 met helder glas en een vierkante greep over de volle lengte</p>
                                </div>
                            </div>
                        </div>

                        <Link
                            href='/configurator'
                            className='absolute -bottom-6 left-6 hidden items-center gap-3 rounded-sm bg-card px-5 py-4 text-foreground shadow-xl ring-1 ring-border transition-transform hover:-translate-y-0.5 sm:flex'
                        >
                            <Box className='size-5 text-primary' aria-hidden='true' />
                            <span>
                                <span className='block text-sm font-semibold'>Maak hem eigen</span>
                                <span className='block text-xs text-muted-foreground'>Elke maat, roeden op de millimeter</span>
                            </span>
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>
                </div>
            </section>

            <section id='introductie' className='mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-36'>
                <div className='grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-end'>
                    <Reveal>
                        <p className='eyebrow'>Over ArtDoors</p>
                        <p className='mt-6 font-display text-4xl leading-[1.12] sm:text-[3.4rem]'>
                            Een stalen deur verandert een ruimte. Hij brengt licht waar het donker was, en rust waar het druk was. Daarom maken we elke deur op maat, voor precies uw opening.
                        </p>
                    </Reveal>

                    <Reveal delay={120} className='flex flex-col gap-6 text-base leading-relaxed text-muted-foreground'>
                        <p>
                            Bij ArtDoors ontwerpt u zelf, met de hulp van onze 3D-configurator. U ziet uw deur in een echte ruimte, plaatst de roeden op de millimeter, kiest helder glas, matglas of glas op aanvraag en kiest zwart, wit of elke RAL-kleur. De prijs ziet u meteen.
                        </p>
                        <p>
                            Daarna nemen wij het over. We meten in, laten uw deur met de hand maken en plaatsen hem met ons eigen team. U volgt elke stap in uw persoonlijke klantomgeving.
                        </p>
                        <Link href='/over-ons' className='inline-flex items-center gap-2 self-start border-b border-primary pb-1 text-sm font-semibold text-foreground'>
                            Meer over ons vakmanschap
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <section id='collectie' className='border-y border-border bg-card'>
                <div className='mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                    <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                        <SectionHeading eyebrow='De collectie' title='Vier modellen, oneindig veel deuren' description='Elk model maken we in uw maat, met uw verdeling, uw glas en uw kleur. Kies het model dat bij uw ruimte past.' />

                        <Link href='/collectie' className='inline-flex shrink-0 items-center gap-2 text-sm font-semibold hover:text-primary'>
                            Alle modellen en opties
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>

                    <div className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                        {DOOR_TYPES.map((type, index) => (
                            <Reveal key={type.id} delay={index * 80}>
                                <Link href={`/configurator?model=${type.id}`} className='group flex h-full flex-col overflow-hidden rounded-md bg-background ring-1 ring-border transition-shadow hover:ring-primary/60'>
                                    <div className='relative aspect-square overflow-hidden bg-gradient-to-b from-[#f0efed] to-[#d6d4d0] px-8 pt-8 pb-0 sm:aspect-[4/5] sm:pt-10'>
                                        <DoorDrawing config={DOOR_PREVIEWS[type.id]} className='size-full origin-bottom transition-transform duration-700 group-hover:scale-[1.03]' title={`${type.name} van ArtDoors`} />
                                    </div>

                                    <div className='flex flex-1 flex-col gap-2 p-6'>
                                        <h3 className='font-display text-3xl'>{type.name}</h3>
                                        <p className='text-sm leading-relaxed text-muted-foreground'>{type.description}</p>

                                        <div className='mt-auto flex items-center justify-between pt-5'>
                                            <span className='text-sm font-semibold'>Bereken uw prijs</span>
                                            <span className='flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform group-hover:translate-x-1'>
                                                <ArrowRight className='size-4' aria-hidden='true' />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>

                </div>
            </section>

            <section id='verdelingen' className='mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                <div className='grid gap-14 lg:grid-cols-[0.9fr_1.4fr] lg:items-center'>
                    <div>
                        <SectionHeading
                            eyebrow='Glasverdeling'
                            title={<>Het karakter zit in de <em className='text-primary italic'>lijnen</em></>}
                            description='Kies een van onze verdelingen of plaats de roeden zelf, tot op de millimeter waar u ze wilt hebben. De glasverdeling is altijd bij de prijs inbegrepen en u ziet het resultaat meteen in 3D.'
                        />

                        <Reveal delay={100}>
                            <Link href='/configurator' className='mt-10 inline-flex h-12 items-center gap-2 rounded-sm bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/85'>
                                Probeer het zelf
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </Link>
                        </Reveal>
                    </div>

                    <div className='grid grid-cols-4 gap-3 sm:grid-cols-8'>
                        {DIVISION_SHOWCASE.map(({ division, config }, index) => (
                            <Reveal key={division.id} delay={index * 60} className='flex flex-col items-center gap-3'>
                                <div className='flex w-full items-end justify-center rounded-sm bg-paper px-2 pt-4 pb-1'>
                                    <DoorDrawing config={config} className='h-auto w-full' title={`Glasverdeling ${division.name}`} />
                                </div>
                                <span className='text-center text-xs font-semibold'>{division.name}</span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section id='materialen' className='border-y border-border bg-ink text-ink-foreground'>
                <div className='mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                    <SectionHeading
                        tone='dark'
                        eyebrow='Materialen'
                        title='Glas dat licht doorlaat, staal dat blijft'
                        description='Altijd gelaagd veiligheidsglas en duurzaam gepoedercoat staal. Kies hoeveel privacy u wilt en welke kleur bij uw interieur past.'
                    />

                    <div className='mt-14 grid gap-10 lg:grid-cols-[1.4fr_1fr]'>
                        <div className='grid gap-4 sm:grid-cols-3'>
                            {GLASS_TYPES.map((glass, index) => (
                                <Reveal key={glass.id} delay={index * 60} className='flex flex-col gap-4'>
                                    <GlassTile glass={glass.id} className='aspect-[3/4] rounded-sm ring-1 ring-white/10' />
                                    <div>
                                        <p className='text-sm font-semibold'>{glass.name}</p>
                                        <p className='mt-1 text-xs leading-relaxed text-ink-foreground/55'>{glass.description}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <Reveal delay={120} className='flex flex-col gap-6 rounded-md bg-white/[0.03] p-6 ring-1 ring-white/10 sm:p-8'>
                            <p className='text-xs font-semibold tracking-[0.2em] text-primary uppercase'>Kleur van de profielen</p>

                            <ul className='flex flex-col gap-4'>
                                {FINISHES.map(finish => (
                                    <li key={finish.id} className='flex items-center gap-4'>
                                        <span
                                            className='size-10 shrink-0 rounded-full ring-1 ring-white/20'
                                            style={{
                                                background: finish.id === 'ral'
                                                    ? 'conic-gradient(from 200deg, #e75b12, #c8a35e, #27352a, #1a2b3c, #75151e, #e75b12)'
                                                    : `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.35), transparent 55%), ${finish.color}`
                                            }}
                                            aria-hidden='true'
                                        />
                                        <span className='text-sm'>
                                            <span className='font-semibold'>{finish.name}</span>
                                            <span className='block text-xs text-ink-foreground/55'>{finish.code}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <div className='border-t border-white/10 pt-5'>
                                <p className='text-xs text-ink-foreground/60'>Een greep uit de RAL-kleuren die u kunt kiezen</p>
                                <div className='mt-3 flex flex-wrap gap-2'>
                                    {RAL_PREVIEW.map(code => {
                                        const color = RAL_COLORS.find(item => item.code === code)

                                        return (
                                            <span key={code} className='inline-flex items-center gap-2 rounded-full bg-white/5 px-2.5 py-1 text-[11px] ring-1 ring-white/10'>
                                                <span className='size-3 rounded-full' style={{ backgroundColor: color?.hex }} aria-hidden='true' />
                                                {code}
                                            </span>
                                        )
                                    })}
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section id='werkwijze' className='mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                    <SectionHeading eyebrow='Werkwijze' title='Van idee tot geplaatste deur' description='Zes heldere stappen. U ontwerpt, wij doen de rest, en u weet altijd waar u aan toe bent.' />

                    <Link href='/werkwijze' className='inline-flex shrink-0 items-center gap-2 text-sm font-semibold hover:text-primary'>
                        Lees meer over onze werkwijze
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </div>

                <div className='mt-14'>
                    <ProcessList />
                </div>
            </section>

            <section id='vakmanschap' className='mx-auto max-w-[1440px] px-5 pb-24 sm:px-8 lg:px-12 lg:pb-32'>
                <div className='grid overflow-hidden rounded-md bg-card ring-1 ring-border lg:grid-cols-2'>
                    <div className='relative min-h-80 lg:min-h-[34rem]'>
                        <video
                            autoPlay
                            muted
                            loop
                            playsInline
                            aria-label='Video van het vakmanschap van ArtDoors'
                            className='absolute inset-0 h-full w-full object-cover'
                        >
                            <source src='https://artdoors-u61.cdn.modulify.website/videos/artdoors.mp4' type='video/mp4' />
                            <source src='https://artdoors-u61.cdn.modulify.website/videos/artdoors.ogg' type='video/ogg' />
                        </video>
                    </div>

                    <div className='flex flex-col justify-center gap-6 p-8 sm:p-12 lg:p-16'>
                        <SectionHeading eyebrow='Vakmanschap' title='Getekend, gelast en gecoat door vakmensen' />

                        <Reveal delay={80} className='flex flex-col gap-5 text-base leading-relaxed text-muted-foreground'>
                            <p>Elke ArtDoors deur begint als een technische tekening op basis van uw ontwerp en de ingemeten maten. Pas als alles klopt, gaat hij in productie.</p>
                            <p>Het staal wordt met de hand gelast, geschuurd en gepoedercoat, het glas exact op maat gesneden. Zo krijgt u een deur die strak sluit en jarenlang mooi blijft.</p>
                        </Reveal>

                        <Reveal delay={140}>
                            <ul className='grid gap-3 text-sm'>
                                {['Gelaagd veiligheidsglas in elke deur', 'Poedercoating in zwart, wit of elke RAL-kleur', 'Strak afgesteld door ons eigen montageteam'].map(item => (
                                    <li key={item} className='flex items-center gap-3'>
                                        <ShieldCheck className='size-4 text-primary' aria-hidden='true' />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section id='klantomgeving' className='border-y border-border bg-card'>
                <div className='mx-auto grid max-w-[1440px] gap-14 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:items-center lg:px-12 lg:py-32'>
                    <div>
                        <SectionHeading
                            eyebrow='Uw persoonlijke omgeving'
                            title='Volg uw deur van werkplaats tot woonkamer'
                            description='Met uw account bewaart u ontwerpen, downloadt u offertes en volgt u na uw bestelling elke stap: aanbetaling, inmeting, productie en montage. Inclusief al uw documenten en afspraken.'
                        />

                        <Reveal delay={100} className='mt-10 flex flex-col gap-3 sm:flex-row'>
                            <Link href='/account/registreren' className='inline-flex h-12 items-center justify-center gap-2 rounded-sm bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/85'>
                                Account aanmaken
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </Link>
                            <Link href='/account/inloggen' className='inline-flex h-12 items-center justify-center rounded-sm border border-foreground/20 px-6 text-sm font-semibold hover:bg-muted'>
                                Inloggen
                            </Link>
                        </Reveal>
                    </div>

                    <Reveal delay={120}>
                        <div className='rounded-md bg-background p-6 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.8)] ring-1 ring-border sm:p-8'>
                            <div className='flex items-start justify-between gap-4'>
                                <div>
                                    <p className='eyebrow'>Voorbeeld van uw bestelling</p>
                                    <p className='mt-2 font-display text-3xl'>Taatsdeur Atelier</p>
                                </div>
                                <span className='rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-brass-strong'>In productie</span>
                            </div>

                            <div className='mt-8'>
                                <OrderProgress status='in_productie' depositPaid />
                            </div>

                            <div className='mt-8 grid gap-4 border-t border-border pt-6 text-sm sm:grid-cols-3'>
                                <div>
                                    <p className='text-xs text-muted-foreground'>Inmeting</p>
                                    <p className='font-medium'>Afgerond</p>
                                </div>
                                <div>
                                    <p className='text-xs text-muted-foreground'>Productie</p>
                                    <p className='font-medium'>Wordt gepoedercoat</p>
                                </div>
                                <div>
                                    <p className='text-xs text-muted-foreground'>Documenten</p>
                                    <p className='font-medium'>Offerte, order, aanbetaling</p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section id='voorbereidende-werken' className='border-y border-border bg-card'>
                <div className='mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                    <SectionHeading
                        eyebrow='Voorbereidende werken'
                        title='Bestaande binnendeur uitbraak + bezetten service'
                        description='Wij halen uw oude binnendeur en kozijn eruit en werken de opening netjes af, klaar voor uw nieuwe stalen deur. Dit bespreken we tijdens het huisbezoek en u krijgt er meteen een prijs voor.'
                    />

                    <div className='mt-14 grid max-w-3xl gap-5 sm:grid-cols-2'>
                        {[
                            { label: 'Voor', src: '/images/voor-uitbraak.jpg', alt: 'Oude witte binnendeur met uitgebroken kozijnrand, voor de werkzaamheden' },
                            { label: 'Na', src: '/images/na-bezetten.jpg', alt: 'Strak afgewerkte opening met een zwarte stalen deur met helder glas' }
                        ].map((item, index) => (
                            <Reveal key={item.label} delay={index * 100} className='relative aspect-[3/4] overflow-hidden rounded-md ring-1 ring-border'>
                                <Image src={item.src} alt={item.alt} fill sizes='(min-width: 640px) 384px, 100vw' className='object-cover' />
                                <span className={`absolute top-4 left-4 rounded-sm px-3 py-1.5 text-xs font-semibold tracking-[0.2em] uppercase ${item.label === 'Na' ? 'bg-primary text-primary-foreground' : 'bg-black/75 text-white'}`}>
                                    {item.label}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section id='veelgestelde-vragen' className='mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                <div className='grid gap-12 lg:grid-cols-[0.8fr_1.2fr]'>
                    <SectionHeading eyebrow='Veelgestelde vragen' title='Goed om te weten' description='Staat uw vraag er niet bij? We denken graag met u mee.' />

                    <Reveal delay={80}>
                        <FaqList limit={5} />
                        <Link href='/werkwijze#veelgestelde-vragen' className='mt-6 inline-flex items-center gap-2 text-sm font-semibold hover:text-primary'>
                            Alle vragen
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <CtaBand />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    let prices = DEFAULT_PRICES

    try {
        const { getPriceList } = await import('@/lib/server/settings')

        prices = await getPriceList()
    }
    catch (error) {
        console.error('[home] prices', error instanceof Error ? error.message : error)
    }

    return { props: { fromPrices: getFromPrices(prices) }, revalidate: 300 }
}

export default Home
